import sanitizeHTML from "@/lib/sanitizeHTML";
import { useEffect, useRef } from "react";

interface ForwardedMessageProps {
    html: string;
    onChange: (html: string) => void;
}

// The forwarded thread: outside the editor and re-joined on send (AB#1746 /
// mirrors mobile), Tiptap would renormalize the original layout. Plain
// contentEditable, always on (AB#1903).
const ForwardedMessage = ({ html, onChange }: ForwardedMessageProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    // What we last wrote, to tell a real edit from a plain focus/blur.
    const writtenHtml = useRef("");
    const write = (element: HTMLDivElement, value: string) => {
        element.innerHTML = sanitizeHTML(value);
        // Read back: the browser normalizes what it parses.
        writtenHtml.current = element.innerHTML;
    };

    // Written straight to the DOM: the body arrives in two steps (cid: then the
    // enriched data:) and a React re-render under the caret would wipe the typing.
    useEffect(() => {
        const element = contentRef.current;

        if (!element) return;

        if (element === document.activeElement) return;

        write(element, html);
    }, [html]);

    if (!html) {
        return null;
    }

    return (
        <div
            ref={contentRef}
            className="mt-2 cursor-text rounded-md p-2 text-text-primary outline-none focus:ring-2 focus:ring-primary-default"
            contentEditable
            onBlur={(e) => {
                if (e.currentTarget.innerHTML !== writtenHtml.current) {
                    onChange(e.currentTarget.innerHTML);
                }
            }}
            suppressContentEditableWarning
        />
    );
};

export default ForwardedMessage;
